import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject, insertAtEnd } from '@/helpers/api'

import { billingOrganizationQueryKey } from '@/hooks/billing/types'
import { useHandleError } from '@/hooks/common/useErrorHandler'
import { organizationsQueryKeys } from '@/hooks/organization/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { BillingOrganization } from '@/types/BillingOrganization'
import type { CreateOrganization, Organization } from '@/types/Organization'

const createOrganization = async (
  billingOrgId: string,
  organization: CreateOrganization,
  accessToken: string | undefined | null,
) => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const res = await fetch(
    `${API_URL}/api/v1/billing-organization/${billingOrgId}/organizations`,
    {
      method: 'POST',
      body: JSON.stringify(organization),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json())
}

export const useCreateOrganization = () => {
  const user = useAuthInfo()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<Organization, Error, [string, CreateOrganization], void>({
    mutationFn: ([billingOrgId, organization]) =>
      createOrganization(billingOrgId, organization, user?.accessToken),
    onError: (err, [billingOrgId, organization], context) => {
      // rollback to the previous value
      // TODO: figure out how to properly handle this error, since it propagates to the user
      handleError(err)
    },
    onSuccess: (data, [billingOrgId, organization]) => {
      const key = organizationsQueryKeys.all()
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<Organization>, PaginateRequest>
      >(key, (oldData) => insertAtEnd(data, oldData))
      queryClient.invalidateQueries({ queryKey: key })

      // Update the billing organization's number of organizations to ensure the UI is in sync
      // and they can't create more organizations if they've hit the limit
      const billingOrgKey = billingOrganizationQueryKey.all()
      const billingOrg =
        queryClient.getQueryData<BillingOrganization>(billingOrgKey)
      if (billingOrg) {
        const updatedBillingOrg: BillingOrganization = {
          ...billingOrg,
          num_organizations: billingOrg.num_organizations + 1,
        }
        queryClient.setQueryData<BillingOrganization>(
          billingOrgKey,
          updatedBillingOrg,
        )
      }
    },
  })
}
